import { useState, useEffect, useCallback } from "react";
import axios from "../lib/api";
import {
  format, subDays, startOfMonth, endOfMonth, subMonths,
  startOfWeek, endOfWeek, subWeeks
} from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  TrendingUp,
  DollarSign,
  ShoppingBag,
  Users,
  Download,
  BarChart2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const fmtCurrency = (v) =>
  typeof v === "number"
    ? v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
    : "—";

const AwinStatusBadge = ({ status }) => {
  const map = {
    approved: { label: "Aprobada", className: "bg-green-100 text-green-700 border-green-200" },
    pending:  { label: "Pendiente", className: "bg-amber-100 text-amber-700 border-amber-200" },
    declined: { label: "Rechazada", className: "bg-red-100 text-red-700 border-red-200" },
  };
  const cfg = map[status?.toLowerCase()] || { label: status || "—", className: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
};

const StoreStatusBadge = ({ status }) => {
  const map = {
    completed: { label: "Completado", className: "bg-blue-100 text-blue-700 border-blue-200" },
    cancelled: { label: "Cancelado",  className: "bg-red-100 text-red-700 border-red-200" },
    refunded:  { label: "Devuelto",   className: "bg-orange-100 text-orange-700 border-orange-200" },
    pending:   { label: "Pendiente",  className: "bg-amber-100 text-amber-700 border-amber-200" },
    unknown:   { label: "No encontrado", className: "bg-slate-100 text-slate-500 border-slate-200" },
  };
  const cfg = map[status?.toLowerCase()] || { label: status || "—", className: "bg-slate-100 text-slate-500" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
};

const ValidBadge = ({ valid, notFound, mismatch }) => {
  if (valid) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
        <CheckCircle2 className="w-3 h-3" /> Válida
      </span>
    );
  }
  if (notFound) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
        <Search className="w-3 h-3" /> No encontrado
      </span>
    );
  }
  if (mismatch) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
        <AlertTriangle className="w-3 h-3" /> Mismatch
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
      <XCircle className="w-3 h-3" /> No válida
    </span>
  );
};

const POSITION_MAP = {
  first:    { label: "1º Primero",    className: "bg-violet-100 text-violet-700 border-violet-200", title: "Awin fue el primer canal y cerró la venta" },
  last:     { label: "Último",        className: "bg-blue-100 text-blue-700 border-blue-200",       title: "Awin cerró la venta (otro canal trajo el usuario)" },
  assist:   { label: "Asistido",      className: "bg-teal-100 text-teal-700 border-teal-200",       title: "Awin trajo al usuario pero otro canal cerró la venta" },
  override: { label: "Override",      className: "bg-red-100 text-red-700 border-red-200",          title: "Awin fue último toque pero un canal de pago llegó primero" },
  unknown:  { label: "Sin datos GA4", className: "bg-slate-100 text-slate-500 border-slate-200",    title: "No se encontró en GA4 — recalcula posiciones" },
};

const PositionBadge = ({ position }) => {
  if (!position) return <span className="text-slate-300 text-xs">—</span>;
  const cfg = POSITION_MAP[position] || { label: position, className: "bg-slate-100 text-slate-500" };
  return (
    <span
      title={cfg.title}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border cursor-help ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
};

const SummaryCard = ({ title, value, subtitle, icon: Icon, iconClass }) => (
  <Card className="bg-white border border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{title}</p>
          <p className="text-2xl font-black tracking-tight text-slate-900">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconClass}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const DATE_PRESETS = [
  { label: "Hoy",           from: () => new Date(),                                to: () => new Date() },
  { label: "Ayer",          from: () => subDays(new Date(), 1),                    to: () => subDays(new Date(), 1) },
  { label: "Últimos 7 días", from: () => subDays(new Date(), 6),                   to: () => new Date() },
  { label: "Últimos 14 días", from: () => subDays(new Date(), 13),                 to: () => new Date() },
  { label: "Últimos 30 días", from: () => subDays(new Date(), 29),                 to: () => new Date() },
  { label: "Esta semana",   from: () => startOfWeek(new Date(), { weekStartsOn: 1 }), to: () => new Date() },
  { label: "Semana pasada", from: () => startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: () => endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
  { label: "Este mes",      from: () => startOfMonth(new Date()),                  to: () => new Date() },
  { label: "Mes pasado",    from: () => startOfMonth(subMonths(new Date(), 1)),    to: () => endOfMonth(subMonths(new Date(), 1)) },
];

export default function AwinTransactionsPage() {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [data, setData] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 29),
    to: new Date(),
  });
  const [pendingDateRange, setPendingDateRange] = useState({
    from: subDays(new Date(), 29),
    to: new Date(),
  });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [filters, setFilters] = useState({
    awin_status: "all",
    store_status: "all",
    valid_only: "all",
    position: "all",
  });

  const token = localStorage.getItem("auth_token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const orgId = localStorage.getItem("organization_id");
  const params = (extra = {}) => ({
    ...(orgId ? { organization_id: orgId } : {}),
    ...extra,
  });

  const buildQueryParams = useCallback(() => {
    const qp = {
      date_from: format(dateRange.from, "yyyy-MM-dd"),
      date_to:   format(dateRange.to,   "yyyy-MM-dd"),
    };
    if (filters.awin_status  !== "all") qp.awin_status  = filters.awin_status;
    if (filters.store_status !== "all") qp.store_status = filters.store_status;
    if (filters.valid_only   !== "all") qp.valid_only   = filters.valid_only === "yes";
    if (orgId) qp.organization_id = orgId;
    return qp;
  }, [dateRange, filters, orgId]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API}/awin/transactions`, {
        params: buildQueryParams(),
        withCredentials: true,
        headers: authHeaders,
      });
      setData(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar transacciones Awin");
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  // Auto-sync on mount: if data is stale (>1h) or never synced, trigger automatically
  useEffect(() => {
    const autoSync = async () => {
      try {
        const statusResp = await axios.get(`${API}/awin/status`, {
          params: params(),
          withCredentials: true,
          headers: authHeaders,
        });
        const { configured, synced_at } = statusResp.data;
        if (!configured) return;

        setLastSyncedAt(synced_at);

        const isStale =
          !synced_at ||
          (Date.now() - new Date(synced_at).getTime()) > 60 * 60 * 1000; // >1h

        if (isStale) {
          setAutoSyncing(true);
          await axios.post(`${API}/awin/sync-transactions`, {}, {
            params: params(),
            withCredentials: true,
            headers: authHeaders,
          });
          setLastSyncedAt(new Date().toISOString());
        }
      } catch (_) {
        // Silent — auto-sync failure should not break the page
      } finally {
        setAutoSyncing(false);
        fetchTransactions();
      }
    };
    autoSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      await axios.post(`${API}/awin/sync-transactions`, {}, {
        params: params(),
        withCredentials: true,
        headers: authHeaders,
      });
      setLastSyncedAt(new Date().toISOString());
      toast.success("Sincronización completada");
      await fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en sincronización");
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadCSV = useCallback(async () => {
    try {
      const qp = buildQueryParams();
      const qs = new URLSearchParams(
        Object.entries(qp).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])
      ).toString();
      const csvUrl = `${API}/awin/transactions/export?${qs}`;
      const resp = await fetch(csvUrl, { headers: { ...authHeaders } });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      const blob = await resp.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `awin_transacciones_${format(dateRange.from,"yyyy-MM-dd")}_${format(dateRange.to,"yyyy-MM-dd")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado correctamente");
    } catch (err) {
      toast.error("Error al descargar CSV");
    }
  }, [buildQueryParams, authHeaders, dateRange]);

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const qp = buildQueryParams();
      const resp = await axios.post(`${API}/awin/recalculate-positions`, {}, {
        params: {
          date_from: qp.date_from,
          date_to: qp.date_to,
          ...(orgId ? { organization_id: orgId } : {}),
        },
        withCredentials: true,
        headers: authHeaders,
      });
      const r = resp.data;
      toast.success(`Posiciones recalculadas: ${r.classified}/${r.processed} transacciones clasificadas, ${r.unknown} sin datos GA4`);
      await fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al recalcular posiciones");
    } finally {
      setRecalculating(false);
    }
  };

  const summary = data?.summary || {};
  const transactions = (data?.transactions || []).filter((t) =>
    filters.position === "all" || (t.affiliate_position || "unknown") === filters.position
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="awin-transactions-page">
        {/* Auto-sync indicator */}
        {autoSyncing && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2" data-testid="awin-auto-syncing-banner">
            <RefreshCw className="w-4 h-4 animate-spin shrink-0" />
            <span>Actualizando datos de Awin automáticamente...</span>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">
              Validación de Transacciones Awin
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Cruce de transacciones con pedidos de la tienda
              {lastSyncedAt && !autoSyncing && (
                <span className="ml-2 text-xs text-slate-400">
                  · Actualizado {format(new Date(lastSyncedAt), "d MMM HH:mm", { locale: es })}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Date picker */}
            <Popover open={datePopoverOpen} onOpenChange={(open) => { setDatePopoverOpen(open); if (open) setPendingDateRange(dateRange); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-slate-200" data-testid="awin-date-picker">
                  <Calendar className="w-4 h-4 mr-2" />
                  {format(dateRange.from, "d MMM", { locale: es })} – {format(dateRange.to, "d MMM yyyy", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  <div className="border-r border-slate-200 p-2 space-y-1 min-w-[170px]">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1">Accesos rápidos</p>
                    {DATE_PRESETS.map((p) => (
                      <button
                        key={p.label}
                        onClick={() => {
                          const range = { from: p.from(), to: p.to() };
                          setDateRange(range);
                          setPendingDateRange(range);
                          setDatePopoverOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 flex flex-col">
                    <CalendarComponent
                      mode="range"
                      selected={pendingDateRange}
                      onSelect={(r) => r && setPendingDateRange(r)}
                      numberOfMonths={2}
                      locale={es}
                    />
                    <div className="border-t border-slate-200 pt-2 mt-1 flex justify-end">
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!pendingDateRange.from}
                        onClick={() => {
                          const range = { from: pendingDateRange.from, to: pendingDateRange.to || pendingDateRange.from };
                          setDateRange(range);
                          setPendingDateRange(range);
                          setDatePopoverOpen(false);
                        }}
                      >
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Sync button */}
            <Button
              onClick={handleSync}
              disabled={syncing || autoSyncing}
              className="bg-slate-900 hover:bg-slate-700 text-white"
              data-testid="awin-sync-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${(syncing || autoSyncing) ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando..." : "Sincronizar ahora"}
            </Button>

            {/* CSV download */}
            {data && (
              <Button
                onClick={handleDownloadCSV}
                variant="outline"
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="awin-download-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar CSV
              </Button>
            )}

            {/* Recalculate positions */}
            <Button
              onClick={handleRecalculate}
              disabled={recalculating}
              variant="outline"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
              data-testid="awin-recalculate-btn"
            >
              <BarChart2 className={`w-4 h-4 mr-2 ${recalculating ? "animate-pulse" : ""}`} />
              {recalculating ? "Calculando..." : "Recalcular posiciones"}
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total transacciones"
            value={summary.total_transactions ?? 0}
            icon={ShoppingBag}
            iconClass="bg-slate-700"
          />
          <SummaryCard
            title="Importe total"
            value={fmtCurrency(summary.total_basket_amount)}
            subtitle={`Neto: ${fmtCurrency(summary.net_revenue_after_commission)}`}
            icon={DollarSign}
            iconClass="bg-blue-600"
          />
          <SummaryCard
            title="Comisiones totales"
            value={fmtCurrency(summary.total_commission)}
            icon={TrendingUp}
            iconClass="bg-amber-500"
          />
          <SummaryCard
            title="Conversiones válidas"
            value={`${summary.total_valid_conversions ?? 0} / ${summary.total_transactions ?? 0}`}
            subtitle={`${summary.total_not_found_in_store ?? 0} sin pedido · ${summary.total_amount_mismatch ?? 0} mismatch`}
            icon={Users}
            iconClass="bg-green-600"
          />
        </div>

        {/* Filters */}
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Filter className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="flex flex-wrap gap-3">
                {/* Awin status */}
                <Select
                  value={filters.awin_status}
                  onValueChange={(v) => setFilters((f) => ({ ...f, awin_status: v }))}
                >
                  <SelectTrigger className="w-40 h-8 text-sm" data-testid="filter-awin-status">
                    <SelectValue placeholder="Estado Awin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="approved">Aprobada</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="declined">Rechazada</SelectItem>
                  </SelectContent>
                </Select>

                {/* Store status */}
                <Select
                  value={filters.store_status}
                  onValueChange={(v) => setFilters((f) => ({ ...f, store_status: v }))}
                >
                  <SelectTrigger className="w-44 h-8 text-sm" data-testid="filter-store-status">
                    <SelectValue placeholder="Estado tienda" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="completed">Completado</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                    <SelectItem value="refunded">Devuelto</SelectItem>
                    <SelectItem value="unknown">No encontrado</SelectItem>
                  </SelectContent>
                </Select>

                {/* Valid only */}
                <Select
                  value={filters.valid_only}
                  onValueChange={(v) => setFilters((f) => ({ ...f, valid_only: v }))}
                >
                  <SelectTrigger className="w-40 h-8 text-sm" data-testid="filter-valid-only">
                    <SelectValue placeholder="Validez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="yes">Solo válidas</SelectItem>
                    <SelectItem value="no">Solo no válidas</SelectItem>
                  </SelectContent>
                </Select>

                {/* Position filter */}
                <Select
                  value={filters.position}
                  onValueChange={(v) => setFilters((f) => ({ ...f, position: v }))}
                >
                  <SelectTrigger className="w-44 h-8 text-sm" data-testid="filter-position">
                    <SelectValue placeholder="Posición afiliado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las posiciones</SelectItem>
                    <SelectItem value="first">Primero</SelectItem>
                    <SelectItem value="last">Último</SelectItem>
                    <SelectItem value="assist">Asistido</SelectItem>
                    <SelectItem value="override">Override</SelectItem>
                    <SelectItem value="unknown">Sin datos GA4</SelectItem>
                  </SelectContent>
                </Select>

                {(filters.awin_status !== "all" || filters.store_status !== "all" || filters.valid_only !== "all" || filters.position !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({ awin_status: "all", store_status: "all", valid_only: "all", position: "all" })}
                    className="h-8 text-slate-500 hover:text-slate-900 text-sm"
                  >
                    Limpiar filtros
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-slate-900">
              Transacciones ({transactions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No hay transacciones para este período</p>
                <p className="text-sm mt-1">
                  Prueba a sincronizar o ampliar el rango de fechas
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 bg-slate-50">
                      <TableHead className="text-slate-600 text-xs font-semibold">Pedido</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold">Afiliado</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold">Fecha venta</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold text-right">Importe</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold text-right">Comisión</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold">Estado Awin</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold">Estado tienda</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold">Validez</TableHead>
                      <TableHead className="text-slate-600 text-xs font-semibold">Posición</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow
                        key={txn.awin_transaction_id}
                        className="border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`awin-txn-row-${txn.awin_transaction_id}`}
                      >
                        <TableCell className="font-mono text-sm text-slate-900">
                          {txn.order_reference || txn.awin_transaction_id}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700 max-w-[160px] truncate">
                          {txn.publisher_name || txn.publisher_id || "—"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                          {txn.sale_date || (txn.transaction_date || "").slice(0, 10)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-slate-900">
                          {fmtCurrency(txn.basket_amount ?? txn.revenue)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-slate-700">
                          {fmtCurrency(txn.commission_amount ?? txn.commission)}
                          {txn.commission_rate > 0 && (
                            <span className="text-xs text-slate-400 ml-1">({txn.commission_rate}%)</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <AwinStatusBadge status={txn.status} />
                        </TableCell>
                        <TableCell>
                          <StoreStatusBadge status={txn.store_order_status} />
                        </TableCell>
                        <TableCell>
                          <ValidBadge
                            valid={txn.is_valid_conversion}
                            notFound={!txn.store_order_found}
                            mismatch={txn.amount_mismatch}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <PositionBadge position={txn.affiliate_position} />
                            {txn.preceding_channels && txn.preceding_channels.length > 0 && (
                              <p className="text-xs text-slate-400 max-w-[180px] truncate" title={txn.preceding_channels.join(" → ")}>
                                {txn.preceding_channels.join(" → ")}
                              </p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
